// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Every colour that carries meaning on the play surface, and — the part that matters — a DECLARATION
// of which surface each one is painted on. `tests/contrast.test.ts` walks `MEANINGFUL` and `PAIRS`
// and holds each to 3:1 (WCAG 2.1 §1.4.11, the floor for a non-text graphic).
//
// This file exists because a sibling game rendered its walls at 1.14:1 against the board: drawn, in
// the right cells, at the right size, and invisible. The screenshot looked atmospheric. Nothing in a
// geometry check, a unit test or a human glance at a dark palette catches that — only a measured
// ratio does. So no renderer in this game is allowed an inline colour literal; if a pixel means
// something, its colour is named here and it is pinned.
//
// ── THREE THINGS THE MEASUREMENT CHANGED ────────────────────────────────────────────────────────
// 1. The first cut drew a near-white car on a bright teal shaft: **1.85:1**. The shaft is now
//    identified by bright RAILS on a deliberately dark WELL, and the car sits on the well at 14.7:1.
//    A background's whole job is to make the thing in front of it visible.
// 2. The "bad" red was #FF6B5C, which measured **2.43:1** against a white person-dot and **2.53:1**
//    against a white car — so a queue going impatient, and a car breaking down, were both changes a
//    player could miss. It is #E0453A now: darker, so it clears 3:1 against the light things it must
//    be told apart from, while still clearing 3:1 against every surface it is drawn on.
// 3. "Vacant vs leased" is NOT a contrast pair, and asserting it was a mistake: a unit fill must be
//    ≥3:1 against the slab (L ≥ 0.188) and a vacant fill would have to be ≤0.113 to be ≥3:1 below the
//    tenancy fills. That window is empty, so the requirement is unsatisfiable and therefore wrong.
//    Vacancy is carried by a HATCH and a "TO LET" glyph instead — a channel contrast cannot eat.
//    Same reasoning for mood: the mood bar is drawn in the slab gap under a unit, never as a tint of
//    the unit's own fill, so its background is `slab` and it is measurable.
//
// Colour-blind safety is NOT carried by hue alone. Every unit type also has a distinct glyph and a
// distinct width in cells, so a deuteranope reading blue-vs-teal by lightness still has two other
// channels.

/** Backgrounds a meaningful colour can be painted onto. */
export const SURFACES = {
  /** The night sky around and above the tower. */
  sky: '#0E1720',
  /** The tower shell: built-but-empty structure, and the slab between floors. */
  slab: '#22303E',
  /** The ground below the lot. */
  ground: '#171F27',
  /** The inside of a lift shaft — the dark well a car travels through. */
  shaftWell: '#0A2129',
  /** DOM panels (HUD, trays, dialogs). */
  panel: '#16202B',
} as const;

export type SurfaceName = keyof typeof SURFACES;

export const COLORS = {
  // ── tenancies ────────────────────────────────────────────────────────────────
  office: '#7FB3FF',
  flat: '#FFC14D',
  shop: '#FF8FB1',
  service: '#C8B4FF',
  /** A built-but-unleased unit. Drawn hatched, with a "TO LET" glyph — see note 3 above. */
  vacant: '#6E8CA8',

  // ── vertical transport ───────────────────────────────────────────────────────
  shaft: '#4FC7C2',
  car: '#EAF6F5',
  /** The load pips drawn ON a car. Dark, because the car is nearly white. */
  carLoad: '#1B2A38',
  stairs: '#B9C6D4',

  // ── people ───────────────────────────────────────────────────────────────────
  person: '#E8F0F8',

  // ── trouble ──────────────────────────────────────────────────────────────────
  // One red, used for every "this is going wrong" signal, chosen so it clears 3:1 against BOTH the
  // dark surfaces it is drawn on and the light things it must be distinguished from.
  /** A person who has waited past their patience. The queue you must be able to see. */
  impatient: '#E0453A',
  /** A car that is out of service (Storm Season). */
  broken: '#E0453A',
  /** A tenancy whose mood has fallen into the danger band. */
  unhappy: '#E0453A',

  // ── overlays ─────────────────────────────────────────────────────────────────
  // A diagnostic overlay draws a BAR PER FLOOR: its LENGTH is the magnitude, its colour is only the
  // binary "is this floor failing". That split is forced by the measurement, not a style choice — a
  // three-stop ramp whose adjacent pairs are each ≥3:1 needs a 9× luminance span between the ends,
  // and two colours that both clear 3:1 on the same dark slab cannot span that. So `heatWarn` is a
  // midpoint tint that is NOT asserted against the ends; length is what carries the middle.
  heatGood: '#A8F0C8',
  heatWarn: '#FFC14D',
  heatBad: '#E0453A',

  // ── chrome painted on the canvas ─────────────────────────────────────────────
  /** The build cursor when a placement is legal. */
  ok: '#5BD990',
  /** The build cursor when a placement is illegal. */
  no: '#E0453A',
  /** Floor numbers and canvas labels. */
  label: '#9FB3C8',
} as const;

export type ColorName = keyof typeof COLORS;

/**
 * The audit list: every meaningful colour, and every surface it can actually be painted on.
 *
 * Adding a colour to the renderer without adding it here is the failure this file exists to prevent,
 * so `tests/contrast.test.ts` also asserts that every key of COLORS appears in exactly one entry.
 */
export const MEANINGFUL: ReadonlyArray<{ color: ColorName; on: readonly SurfaceName[] }> = [
  { color: 'office', on: ['slab', 'sky'] },
  { color: 'flat', on: ['slab', 'sky'] },
  { color: 'shop', on: ['slab', 'sky'] },
  { color: 'service', on: ['slab', 'sky'] },
  { color: 'vacant', on: ['slab', 'sky'] },
  { color: 'shaft', on: ['slab', 'sky', 'shaftWell'] },
  { color: 'car', on: ['shaftWell'] },
  { color: 'stairs', on: ['slab', 'sky'] },
  { color: 'person', on: ['slab', 'ground'] },
  { color: 'impatient', on: ['slab', 'ground'] },
  { color: 'broken', on: ['shaftWell', 'slab'] },
  { color: 'unhappy', on: ['slab'] },
  { color: 'heatGood', on: ['slab', 'sky'] },
  { color: 'heatWarn', on: ['slab', 'sky'] },
  { color: 'heatBad', on: ['slab', 'sky'] },
  { color: 'ok', on: ['slab', 'sky'] },
  { color: 'no', on: ['slab', 'sky'] },
  { color: 'label', on: ['sky', 'ground', 'panel'] },
];

/**
 * Pairs that are meaningful-colour-against-meaningful-colour rather than colour-against-surface.
 * These are the ones a screenshot flatters and a measurement does not.
 */
export const PAIRS: ReadonlyArray<{ a: ColorName; b: ColorName; why: string }> = [
  { a: 'carLoad', b: 'car', why: 'how full a car is, read at a glance from the shaft' },
  { a: 'broken', b: 'car', why: 'an out-of-service car must not read as a working one' },
  { a: 'impatient', b: 'person', why: 'the queue that is about to cost you a tenant' },
  { a: 'heatBad', b: 'heatGood', why: 'the two ends of every diagnostic overlay' },
];

/**
 * `carLoad` is drawn only ON a car, never on the bare well — so `PAIRS` carries it and MEANINGFUL
 * must not claim the well as its background. Kept as an explicit exception so the test can check
 * the coverage of COLORS without a special case going unnoticed.
 */
export const NOT_ON_SURFACE: readonly ColorName[] = ['carLoad'];
